import type { IBusiness } from "@/business/business";
import type { IPayPeriod } from "@/payroll/payroll.d";
import { isPayPeriodLocked } from "@/payroll/utils/period-helpers";
import { isAdmin } from "@/user/utils";

export const LOCKED_EXPLANATION =
  "Payroll for this period is final. It cannot be rerun or edited, and only Volca can unlock it.";

export const LOCKED_RERUN_REASON = "Locked payroll cannot be rerun";

/**
 * Whether to offer this person the lock.
 *
 * Full-workbook authority and nothing less, and unlike `canRerunFullPayroll` this
 * demands an explicit `true`: that helper reads an absent field as permission,
 * because the cost of hiding a rerun during deploy skew is worse than a control
 * that 403s. Locking is the other way round. It is terminal, only Volca can undo
 * it, and offering it to someone the API will refuse is a worse trade than making
 * them wait for the field to arrive. The endpoint checks the same authority
 * (`Spreadsheet::AccessResolver.full_workbook_authority?`) either way. During
 * impersonation, the capability already represents the impersonated user, so
 * impersonation is not a separate input to this decision.
 */
export const canLockPayPeriod = ({
  period,
  business,
  user,
}: {
  period: IPayPeriod | null | undefined;
  business: IBusiness | null | undefined;
  user: { role?: string } | null | undefined;
}) =>
  !!period &&
  !isPayPeriodLocked(period) &&
  !isAdmin(user?.role) &&
  business?.can_rerun_full_payroll === true;

export interface PayPeriodLockTarget {
  businessId: string | null;
  period: IPayPeriod;
}
